import axios from 'axios'
import * as React from 'react'
import { FC, useEffect, useState } from 'react'
import config from 'src/config'
import LoadIcon from '../LoadIcon'
import HallSessions from './HallSessions'
import { ProgrammeStyle } from './styled'

export const VIRTUAL_HALLS: Array<ProgrammeSession['hall']> = [
  'Virtual Hall A',
  'Virtual Hall B',
]

export const MTE_HALLS: Array<ProgrammeSession['hall']> = ['Mte']

export const WORKSHOP_HALLS: Array<ProgrammeSession['hall']> = ['Workshop']

const EVENT_DATES = [
  { dayNum: 1, date: '09 AUGUST | TUESDAY' },
  { dayNum: 2, date: '10 AUGUST | WEDNESDAY' },
  { dayNum: 3, date: '11 AUGUST | THURSDAY' },
]

export const FACULTY_TYPE = [
  'Chairperson',
  'Emcee',
  'Judge',
  'Guest Of Honor',
  'Speaker',
  'Adjudicator',
] as const

export enum VENUE_TYPE {
  'virtual',
  'mte',
  'workshop',
}

export interface Faculty {
  id: number
  fullName: string
  avatar: string
  shortProfile: string
}

export interface FacultyWithType extends Faculty {
  facultyType: typeof FACULTY_TYPE[number]
}

export interface ProgrammeSession {
  id: number
  title: string
  desc?: string
  startAt: string
  endAt: string
  date: string // 11 JUN | SATURDAY
  time: string // 10:00 to 11:00
  day: 1 | 2 | 3
  hall: 'Virtual Hall A' | 'Virtual Hall B' | 'Mte' | 'Workshop'
  faculties: Array<FacultyWithType>
  subsessions: Array<ProgrammeSubsession>
  secondsTillStart: number
  durationInSeconds: number
  ended?: boolean
  hasPlayback?: boolean
}

export interface ProgrammeSubsession {
  id: number
  title?: string
  description?: string
  speakers: Array<Faculty>
  timeslots: Array<ProgrammeTimeslot>
}

export interface ProgrammeTimeslot {
  id: number
  title: string
  time: string
  speakers: Array<Faculty>
}

const Programme = ({ defaultDay, defaultHall }) => {
  const [loading, setLoading] = useState(false)
  const [venue, setVenue] = useState<VENUE_TYPE>(VENUE_TYPE.virtual)
  const [day, setDay] = useState(defaultDay || 1)
  const [data, setData] = useState<ProgrammeSession[]>([])

  const [activeVirtual, setActiveVirtual] =
    useState<ProgrammeSession['hall']>(defaultHall === 'B' ? 'Virtual Hall B' : 'Virtual Hall A')
  const [activeMte, setActiveMte] = useState<ProgrammeSession['hall']>('Mte')
  const [activeWorkshop, setActiveWorkshop] =
    useState<ProgrammeSession['hall']>('Workshop')

  useEffect(() => {
    setLoading(true)
    axios
      .get<ProgrammeSession[]>(
        `${config.apiUrl}/api/programme_sessions?venue=${VENUE_TYPE[venue]}&day=${day}`
      )
      .then((response) => {
        setData(response.data)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [day, venue])

  const venueClass = 'btn btn-outline-danger my-1 font-weight-bold mr-2'
  const dayClass = 'btn btn-outline-primary my-1 font-weight-bold mr-2'

  if (loading) {
    return (
      <LoadIcon />
    )
  }

  return (
    <ProgrammeStyle>
      <div className="pb-5" id="programmeAgenda">
        <div className="filterBtns">
          <div className="my-3 text-center">
            <small className="d-block mb-1">VENUE</small>
            <button
              className={`${
                venue === VENUE_TYPE.virtual && 'active'
              } ${venueClass}`}
              onClick={() => setVenue(VENUE_TYPE.virtual)}
            >
              Virtual Hall
            </button>
            <button
              className={`${venue === VENUE_TYPE.mte && 'active'} ${venueClass}`}
              onClick={() => setVenue(VENUE_TYPE.mte)}
            >
              Meet The Expert
            </button>
            <button
              className={`${
                venue === VENUE_TYPE.workshop && 'active'
              } ${venueClass}`}
              onClick={() => setVenue(VENUE_TYPE.workshop)}
            >
              Workshop
            </button>
          </div>
          {!defaultDay && (
            <div className="text-center">
              <small className="d-block mb-1">DATE</small>
              {EVENT_DATES.map(({ dayNum, date }) => (
                <button
                  key={dayNum}
                  className={`${dayNum === day && 'active'} ${dayClass}`}
                  onClick={() => setDay(dayNum)}
                >
                  {date}
                </button>
              ))}
            </div>
          )}
        </div>

        {venue === VENUE_TYPE.virtual && (
          <div className="mt-5">
            <div className="d-md-none">
              {VIRTUAL_HALLS.map((hall) => (
                <button
                  key={hall}
                  className={`hallTabs text-white font-weight-bold ${
                    activeVirtual !== hall && 'deselected'
                  }`}
                  onClick={() => setActiveVirtual(hall)}
                >
                  {hall}
                </button>
              ))}
            </div>
            <div className="row">
              {VIRTUAL_HALLS.map((hall) => (
                <div className="col-md-6" key={hall}>
                  <HallSessions
                    hallName={hall}
                    activeHall={activeVirtual}
                    programmes={data.filter(
                      (programme) => programme.hall === hall
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {venue === VENUE_TYPE.mte && (
          <div className="mt-5">
            <div className="d-md-none">
              {MTE_HALLS.map((hall) => (
                <button
                  key={hall}
                  className={`hallTabs text-white font-weight-bold ${
                    activeMte !== hall && 'deselected'
                  }`}
                  onClick={() => setActiveMte(hall)}
                >
                  Meet The Expert
                </button>
              ))}
            </div>
            <div className="row">
              {MTE_HALLS.map((hall) => (
                <div className="col-md-6" key={hall}>
                  <HallSessions
                    hallName={hall}
                    activeHall={activeMte}
                    programmes={data.filter(
                      (programme) => programme.hall === hall
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {venue === VENUE_TYPE.workshop && (
          <div className="mt-5">
            <div className="d-md-none">
              {WORKSHOP_HALLS.map((hall) => (
                <button
                  key={hall}
                  className={`hallTabs text-white font-weight-bold ${
                    activeWorkshop !== hall && 'deselected'
                  }`}
                  onClick={() => setActiveWorkshop(hall)}
                >
                  {hall}
                </button>
              ))}
            </div>
            <div className="row">
              {WORKSHOP_HALLS.map((hall) => (
                <div className="col-md-6" key={hall}>
                  <HallSessions
                    hallName={hall}
                    activeHall={activeWorkshop}
                    programmes={data.filter(
                      (programme) => programme.hall === hall
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ProgrammeStyle>
  )
}

export default Programme
